/** "Couldn't be answered", said in plain words (item 86, Segev 2026-11-02):
 *  conversations a template read but got no answer from - never in a chart -
 *  split by why, the parts adding up to the count shown:
 *   - not enough in the conversation to answer (never asked again while it is unchanged);
 *   - couldn't be processed, so later runs try again (up to MAX_TRIES times);
 *   - couldn't be processed after MAX_TRIES tries.
 *  The server counts them (`unanswered_split`, insights_analytics_repository). */
import type { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { UnansweredSplit } from '../../types/insights';
import { formatCount } from './templateParts';

/** The server's MAX_TRIES (insight_template_state.py): how often a conversation that could not be processed is tried. */
export const MAX_TRIES = 3;

const PARTS: (keyof UnansweredSplit)[] = ['no_basis', 'retrying', 'given_up'];

/** Every template's split added up (the dashboard's total). */
export function addSplits(splits: (UnansweredSplit | undefined)[]): UnansweredSplit {
  const out: UnansweredSplit = { no_basis: 0, retrying: 0, given_up: 0 };
  splits.forEach((s) => PARTS.forEach((k) => { out[k] += s?.[k] || 0; }));
  return out;
}

/** What the tooltip says: what the count is, then each reason with its number
    (every reason when `all`, so the dashboard explains them before any occurs). */
export function CouldntWhyText({ split, all }: { split: UnansweredSplit; all?: boolean }) {
  const { t } = useLanguage();
  return (
    <div className="tpl-why" data-testid="templates-couldnt-why">
      <p className="tpl-why__lead">{t('insights.tpl.why.lead')}</p>
      <ul className="tpl-why__list">
        {PARTS.filter((k) => all || split[k] > 0).map((k) => (
          <li key={k} className="tpl-why__item" data-testid={`templates-couldnt-${k}`}>
            <bdi className="tpl-why__n">{formatCount(split[k])}</bdi>
            <span>{t(`insights.tpl.why.${k}`, { tries: MAX_TRIES })}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A "Couldn't be answered" label or count with its explanation on hover, focus or a tap.
    `split` is the server's; without one (an older server) the whole `total` could not be processed. */
export function CouldntWhy({ split, total, all, children, className, testId }: {
  split?: UnansweredSplit; total: number; all?: boolean; children: ReactNode; className?: string; testId?: string;
}) {
  const why = split || { no_basis: 0, retrying: total, given_up: 0 };
  // A tap opens it on a touch screen (focus and click together would open and close it at once).
  const touch = useMediaQuery('(hover: none)');
  return (
    <Tooltip title={<CouldntWhyText split={why} all={all} />} trigger={touch ? ['click'] : ['hover', 'focus']}
             overlayClassName="tpl-why-tip">
      <button type="button" className={`tpl-why__trigger${className ? ` ${className}` : ''}`} data-testid={testId}>
        <InfoCircleOutlined aria-hidden />
        {children}
      </button>
    </Tooltip>
  );
}
